import classNames from "classnames";
import { IWebsiteBuilderElementBase, IWebsiteBuilderElementDataBase, TWebsiteBuilderElement } from "./websiteBuilderElement";
import { websiteBuilderNormalizeHexColor, websiteBuilderNormalizeValue, websiteBuilderNormalizeDatabaseId } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { WB_IMAGE_HOVER_OVERLAY_COLOR_CSS_VARIABLE, WB_IMAGE_HOVER_OVERLAY_OPACITY_CSS_VARIABLE, WB_IMAGE_HOVER_SCALE_CSS_VARIABLE } from "../../website/shared/cssVariables";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderSectionEntityStretch, websiteBuilderGenerateDefaultSectionEntityStretch } from "../websiteBuilderSectionEntity";
import { CSSProperties } from "react";
import { clamp } from "lodash";

export const WEBSITE_BUILDER_IMAGE_MIN_BORDER_RADIUS = 0;
export const WEBSITE_BUILDER_IMAGE_MAX_BORDER_RADIUS = 100;
export const WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MIN = 2;
export const WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MAX = 10;
const WEBSITE_BUILDER_IMAGE_HOVER_SCALE_DEFAULT = 4;
export const WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MIN = 10;
export const WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MAX = 80;
const WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_DEFAULT = 40;

export type TWebsiteBuilderImageObjectFit = "cover" | "contain" | "fill";

function isWebsiteBuilderImageObjectFit(value: string): value is TWebsiteBuilderImageObjectFit {
  return value === "cover" || value === "contain" || value === "fill";
}

export interface IWebsiteBuilderImageSettings {
  mediaId: number;
  src: string;
  objectFit: TWebsiteBuilderImageObjectFit;
  alt: string;
  borderRadius: number;
  expandOnClick: boolean;
  hoverScale: boolean;
  hoverScaleAmount: number;
  hoverOverlay: boolean;
  hoverOverlayColor: string;
  hoverOverlayOpacity: number;
}

interface IWebsiteBuilderImageData extends IWebsiteBuilderElementDataBase {
  type: "image";
  settings: IWebsiteBuilderImageSettings;
  stretch: IWebsiteBuilderSectionEntityStretch;
}

export interface IWebsiteBuilderImage extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderImageData;
}

export function isWebsiteBuilderImage(node: TWebsiteBuilderElement): node is IWebsiteBuilderImage {
  return node?.element?.type === "image";
}

function generateDefaultImageSettings(mediaId: number, src: string): IWebsiteBuilderImageSettings {
  return {
    mediaId,
    src,
    objectFit: "cover",
    alt: "",
    borderRadius: WEBSITE_BUILDER_IMAGE_MIN_BORDER_RADIUS,
    expandOnClick: false,
    hoverScale: false,
    hoverScaleAmount: WEBSITE_BUILDER_IMAGE_HOVER_SCALE_DEFAULT,
    hoverOverlay: false,
    hoverOverlayColor: null,
    hoverOverlayOpacity: WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_DEFAULT,
  };
}

export function websiteBuilderGenerateDefaultImage(mediaId: number, src: string): IWebsiteBuilderImage {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "image",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 150, height: 150 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 150, height: 150 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 150, height: 150 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultImageSettings(mediaId, src),
      stretch: websiteBuilderGenerateDefaultSectionEntityStretch(),
    },
  };
}

export function websiteBuilderNormalizeImageSettings(partialSettings: Partial<IWebsiteBuilderImageSettings>): IWebsiteBuilderImageSettings {
  const defaultSettings = generateDefaultImageSettings(null, null);

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { mediaId, src, objectFit, alt, borderRadius, expandOnClick, hoverScale, hoverScaleAmount, hoverOverlay, hoverOverlayColor, hoverOverlayOpacity } = partialSettings;

  const normalizedImageSettings: IWebsiteBuilderImageSettings = {
    mediaId: websiteBuilderNormalizeDatabaseId(mediaId, defaultSettings.mediaId, false),
    src: websiteBuilderNormalizeValue({
      type: "string",
      value: src,
      defaultValue: defaultSettings.src,
      required: false,
    }),
    objectFit: websiteBuilderNormalizeValue({
      type: "string",
      value: objectFit,
      defaultValue: defaultSettings.objectFit,
      required: true,
      validate: isWebsiteBuilderImageObjectFit,
    }) as TWebsiteBuilderImageObjectFit,
    alt: websiteBuilderNormalizeValue({
      type: "string",
      value: alt,
      defaultValue: defaultSettings.alt,
      required: true,
    }),
    borderRadius: websiteBuilderNormalizeValue({
      type: "number",
      value: borderRadius,
      defaultValue: defaultSettings.borderRadius,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_IMAGE_MIN_BORDER_RADIUS,
        WEBSITE_BUILDER_IMAGE_MAX_BORDER_RADIUS,
      ),
    }),
    expandOnClick: websiteBuilderNormalizeValue({
      type: "boolean",
      value: expandOnClick,
      defaultValue: defaultSettings.expandOnClick,
      required: true,
    }),
    hoverScale: websiteBuilderNormalizeValue({
      type: "boolean",
      value: hoverScale,
      defaultValue: defaultSettings.hoverScale,
      required: true,
    }),
    hoverScaleAmount: websiteBuilderNormalizeValue({
      type: "number",
      value: hoverScaleAmount,
      defaultValue: defaultSettings.hoverScaleAmount,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MIN,
        WEBSITE_BUILDER_IMAGE_HOVER_SCALE_MAX,
      ),
    }),
    hoverOverlay: websiteBuilderNormalizeValue({
      type: "boolean",
      value: hoverOverlay,
      defaultValue: defaultSettings.hoverOverlay,
      required: true,
    }),
    hoverOverlayColor: websiteBuilderNormalizeHexColor(hoverOverlayColor, defaultSettings.hoverOverlayColor, false),
    hoverOverlayOpacity: websiteBuilderNormalizeValue({
      type: "number",
      value: hoverOverlayOpacity,
      defaultValue: defaultSettings.hoverOverlayOpacity,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MIN,
        WEBSITE_BUILDER_IMAGE_HOVER_OVERLAY_OPACITY_MAX,
      ),
    }),
  };

  return normalizedImageSettings;
}

interface IWebsiteBuilderImageDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  containerClassName: string;
  containerStyleProperties: CSSProperties;
  containerStyleDeclaration: string;
}

export function websiteBuilderGenerateImageDesignStyles(settings: IWebsiteBuilderImageSettings): IWebsiteBuilderImageDesignStyles {
  const hoverOverlayEnabled = settings.hoverOverlay && settings.hoverOverlayColor != null;
  const hoverOverlayOpacity = `${settings.hoverOverlayOpacity}%`;
  const hoverScale = settings.hoverScale ? 1 + settings.hoverScaleAmount / 100 : null;

  const containerStyleProperties: CSSProperties = {
    ...(hoverOverlayEnabled ? {
      borderRadius: settings.borderRadius,
      [WB_IMAGE_HOVER_OVERLAY_COLOR_CSS_VARIABLE]: settings.hoverOverlayColor,
      [WB_IMAGE_HOVER_OVERLAY_OPACITY_CSS_VARIABLE]: hoverOverlayOpacity,
    } : {}),
    ...(hoverScale != null ? { [WB_IMAGE_HOVER_SCALE_CSS_VARIABLE]: String(hoverScale) } : {}),
  };

  let containerStyleDeclaration = "";

  if (hoverOverlayEnabled) {
    containerStyleDeclaration += `border-radius: ${settings.borderRadius}px;`;
    containerStyleDeclaration += `${WB_IMAGE_HOVER_OVERLAY_COLOR_CSS_VARIABLE}: ${settings.hoverOverlayColor};`;
    containerStyleDeclaration += `${WB_IMAGE_HOVER_OVERLAY_OPACITY_CSS_VARIABLE}: ${hoverOverlayOpacity};`;
  }
  if (hoverScale != null) {
    containerStyleDeclaration += `${WB_IMAGE_HOVER_SCALE_CSS_VARIABLE}: ${hoverScale};`;
  }

  const styleProperties: CSSProperties = {
    objectFit: settings.objectFit,
    borderRadius: settings.borderRadius,
  };

  let styleDeclaration = "";

  styleDeclaration += `object-fit: ${styleProperties.objectFit};`;

  if (styleProperties.borderRadius != null) {
    styleDeclaration += `border-radius: ${styleProperties.borderRadius}px;`;
  }
  
  return {
    containerClassName: classNames("website-builder-image-container", {
      "no-src-border": !settings.src,
      "image-hover-scale": settings.hoverScale,
      "image-hover-overlay": hoverOverlayEnabled,
    }),
    containerStyleProperties,
    containerStyleDeclaration,
    className: "website-builder-image",
    styleProperties,
    styleDeclaration,
  };
}